<script lang="ts">
	import { Container, Modal, Text, Button, IconButton } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import SelectMainStateFilter from '../../SelectMainStateFilter.svelte';
	import SelectApplianceTypeFilter from 'src/components/SelectApplianceTypeFilter.svelte';
	import SelectSecondaryStateFilter from 'src/components/SelectSecondaryStateFilter.svelte';
	import SelectAdditionalFunctionsFilter from 'src/components/SelectAdditionalFunctionsFilter.svelte';
	import { devicesFilters, getSelectedDeviceFilterCount } from 'src/stores/ui/filters';
	import type { DevicesFilters } from 'src/stores/ui/filters';
	import { capFirstChar, deepCopy } from 'src/helpers/utilities';
	import SelectRoomFilter from 'src/components/SelectRoomFilter.svelte';
	import SelectSharedDevicesFilter from 'src/components/SelectSharedDevicesFilter.svelte';
	import SelectFirmwareVersionFilter from 'src/components/SelectFirmwareVersionFilter.svelte';
	import { getDevicesCurrentApplianceTypes } from 'src/helpers/device_settings_helpers';

	export let openFilterModal;

	let appliedFilters: DevicesFilters = deepCopy($devicesFilters);

	const components = {
		rooms: SelectRoomFilter,
		device_state: SelectMainStateFilter,
		shared_devices: SelectSharedDevicesFilter,
		firmware_version: SelectFirmwareVersionFilter,
		appliance_types: SelectApplianceTypeFilter,
		cloud_connectivity: SelectSecondaryStateFilter,
		more_filters: SelectAdditionalFunctionsFilter,
	};

	let deviceFilters = Object.keys(appliedFilters).map((filter) => ({
		title: `text_title_devices_filter_${filter}`,
		component: components[filter],
	}));

	// if don't have any device with appliance type -> hide the title Appliance types
	if (getDevicesCurrentApplianceTypes().length === 0) {
		deviceFilters = deviceFilters.filter(
			(filter) => filter.title !== 'text_title_devices_filter_appliance_types',
		);
	}

	const handleFilter = () => {
		devicesFilters.set(appliedFilters);
		openFilterModal = false;
	};

	$: notification = getSelectedDeviceFilterCount(appliedFilters);

	function resetData() {
		// raise an event so all children components can know to reset their ui
		const resetEvent = new CustomEvent('reset');
		dispatchEvent(resetEvent);
		notification = getSelectedDeviceFilterCount(appliedFilters);
	}

	const toggleFilterOptions = (filterOptions, selectedValue?) => {
		return filterOptions.map((option) => {
			if (option.id === selectedValue.id) {
				option.selected = !option.selected;
				return option;
			}
			return option;
		});
	};
</script>

<Modal
	title={$format('modal_apply_devices_filters', {
		default: 'Filters',
	})}
	showModal={openFilterModal}
	on:close={() => {
		openFilterModal = false;
	}}
	backdropClose
	{notification}
	removeScrollPadding
>
	<Container fullHeight slot="content" gap="s" padding="scrollPaddings">
		{#each deviceFilters as { title, component }, i}
			<Text class="mb-s" element="h2"
				>{$format(`${title}`, {
					default: title.split('_').slice(4).join('_'),
				})}</Text
			>
			<svelte:component this={component} bind:appliedFilters {toggleFilterOptions} />
			{#if i < deviceFilters.length - 1}
				<hr class="mt-l mb-l" />
			{/if}
		{/each}
	</Container>
	<div slot="footer" class="d-flex flex-direction-column">
		<IconButton
			on:click={resetData}
			icon="fas fa-xmark"
			variant="secondary"
			class="width-full"
			disabled={!notification}
		>
			{$format('button_filter_modal_clear_all', { default: 'Clear all' })}
		</IconButton>
		<div class="d-flex justify-content-end mt-md-l mt-sm-m">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (openFilterModal = false)}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={handleFilter}
			>
				{capFirstChar($format('button_apply', { default: 'Apply' }))}
			</Button>
		</div>
	</div>
</Modal>
